import { useCallback, useMemo } from 'react';

import { useGetBaseVariant } from '../../documents_v2/hooks/useGetBaseVariant';
import { useGetDocument } from '../../documents_v2/hooks/useGetDocument';
import { useGetDocumentVariants } from '../../documents_v2/hooks/useGetDocumentVariants';

const includeAllVariants = () => true;

/**
 * Shared handlers for the tag modals: edits, toggles and copying of tag items.
 * Handlers are stable across renders (they read/write through immer drafts) so
 * that memoized TagRow components do not re-render on unrelated changes.
 *
 * @param {{formData: import('models/documents.models').Tag, showAllVariants: boolean}} state current immer state
 * @param {function(function(object):void):void} setState immer state updater
 * @param {(variantId: number) => boolean} [isVariantIncluded] restricts which variants copyItem writes to
 */
export function useTagFormHandlers(state, setState, isVariantIncluded = includeAllVariants) {
    const document = useGetDocument();
    const baseVariant = useGetBaseVariant();
    const documentVariants = useGetDocumentVariants();

    const { tagItems = [], tagValue } = state.formData ?? {};

    const baseItem = document
        ? tagItems.find((_item) => _item.documentVariantId === (baseVariant?.id ?? ''))
        : undefined;

    const defaultItem = useMemo(() => {
        return {
            ...tagValue,
            ...baseItem,
        };
    }, [tagValue, baseItem]);

    const toggleVariantItem = useCallback(
        (checked, variant) => {
            setState((draft) => {
                const tagItems = draft.formData.tagItems ?? [];
                const currentItem = tagItems.find((_item) => _item.documentVariantId === variant.id);

                if (checked === true && currentItem === undefined) {
                    // Item not defined yet
                    draft.formData.tagItems = tagItems.concat([
                        {
                            documentVariantId: variant.id,
                            text: defaultItem?.text ?? '',
                            url: defaultItem?.url ?? '',
                            tooltip: defaultItem?.tooltip ?? '',
                            crossReference: defaultItem?.crossReference ?? '',
                        },
                    ]);
                } else if (checked === false && currentItem !== undefined) {
                    // Remove existing item
                    draft.formData.tagItems = tagItems.filter((item) => item.documentVariantId !== variant.id);
                }
            });
        },
        [setState, defaultItem],
    );

    const copyItem = useCallback(
        (item) => {
            setState((draft) => {
                const tagItems = draft.formData.tagItems ?? [];
                const isOverride = draft.formData.isOverride;
                const newTagItems = [item];

                documentVariants.forEach((_variant) => {
                    if (_variant.parentId !== null && _variant.enabled === true && _variant.variantGroup === false) {
                        const currentItem = tagItems.find((_item) => _item.documentVariantId === _variant.id);
                        const itemHasOverride = isOverride && currentItem !== undefined;

                        if ((isOverride === false || itemHasOverride === true) && isVariantIncluded(_variant.id)) {
                            newTagItems.push({
                                documentVariantId: _variant.id,
                                text: item.text,
                                url: item.url,
                                tooltip: item.tooltip,
                                crossReference: item.crossReference,
                            });
                        } else {
                            if (currentItem) {
                                newTagItems.push(currentItem);
                            }
                        }
                    }
                });

                draft.formData.tagItems = newTagItems;
            });
        },
        [setState, documentVariants, isVariantIncluded],
    );

    const handleFormChange = useCallback(
        (value, propName, variantId) => {
            if (variantId) {
                // Store value for the specific Variant, creating the entry if missing.
                setState((draft) => {
                    const items = draft.formData.tagItems ?? [];
                    const index = items.findIndex((item) => item.documentVariantId === variantId);

                    if (index === -1) {
                        items.push({
                            documentVariantId: variantId,
                            text: '',
                            url: '',
                            tooltip: '',
                            crossReference: '',
                            [propName]: value,
                        });
                    } else {
                        items[index][propName] = value;
                    }

                    draft.formData.tagItems = items;
                });
            } else {
                setState((draft) => {
                    if (
                        propName === 'text' ||
                        propName === 'url' ||
                        propName === 'tooltip' ||
                        propName === 'crossReference'
                    ) {
                        if (draft.formData.tagValue) {
                            draft.formData.tagValue[propName] = value;
                        } else {
                            draft.formData.tagValue = {
                                [propName]: value,
                            };
                        }
                    } else {
                        draft.formData[propName] = value;
                    }
                });
            }
        },
        [setState],
    );

    return {
        handleFormChange,
        toggleVariantItem,
        copyItem,
        defaultItem,
    };
}
